import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';
const AuthContext = createContext();

// How often to check if the session is still valid (5 minutes)
const AUTH_CHECK_INTERVAL_MS = 5 * 60 * 1000;

// Legacy key - kept for cleanup of old localStorage entries
const LEGACY_SESSION_ID_KEY = 'printPortalSessionId';

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const isAuthenticated = !!currentUser;
  
  // Track if we're currently handling a 401 to prevent cascading logouts
  const handlingUnauthorized = useRef(false);

  /**
   * Handle 401 Unauthorized responses by logging the user out
   * and showing a session expired message.
   */
  const handleUnauthorized = useCallback(() => {
    // Prevent multiple simultaneous logout attempts
    if (handlingUnauthorized.current) return;
    handlingUnauthorized.current = true;
    
    console.log('Session expired or unauthorized - logging out');
    setCurrentUser(null);
    setSessionExpired(true);
    
    // Clean up any legacy localStorage entries
    localStorage.removeItem(LEGACY_SESSION_ID_KEY);
    
    // Reset the flag after a short delay
    setTimeout(() => {
      handlingUnauthorized.current = false;
    }, 1000);
  }, []);

  /**
   * Wrapper around fetch that automatically handles 401 responses.
   * Use this for all authenticated API calls.
   */
  const authFetch = useCallback(async (url, options = {}) => {
    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'include', // Always include cookies
      });

      // Always treat 401 as session invalid.
      // This catches the case where the UI still thinks you're logged in.
      if (response.status === 401) {
        handleUnauthorized();
      }

      return response;
    } catch (error) {
      // Network errors - don't logout, might be temporary
      console.error('Network error in authFetch:', error);
      throw error;
    }
  }, [handleUnauthorized]);

  // Setup axios interceptor for FastAPI requests (legacy support)
  // Note: axios interceptor now just handles 401s - cookies are sent automatically
  useEffect(() => {
    if (typeof window.axios !== 'undefined') {
      // Response interceptor for 401 handling
      window.axios.interceptors.response.use(
        response => response,
        error => {
          if (error.response && error.response.status === 401) {
            handleUnauthorized();
          }
          return Promise.reject(error);
        }
      );
    }
  }, [handleUnauthorized]);
  
  // Clean up legacy localStorage on mount
  useEffect(() => {
    // Remove old localStorage token if present (migrated to httpOnly cookie)
    const legacyToken = localStorage.getItem(LEGACY_SESSION_ID_KEY);
    if (legacyToken) {
      console.log('Cleaning up legacy localStorage session token (now using httpOnly cookie)');
      localStorage.removeItem(LEGACY_SESSION_ID_KEY);
    }
  }, []);
  
  const UPLOAD_API_URL = process.env.REACT_APP_UPLOAD_API_URL || '';

  const checkFlaskAuth = useCallback(async () => {
    const response = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
    if (!response.ok) {
      return { ok: false };
    }
    const data = await response.json();
    if (!data.success) {
      return { ok: false };
    }
    return { ok: true, user: data.user };
  }, []);

  const checkUploadAuth = useCallback(async () => {
    if (!UPLOAD_API_URL) {
      return { ok: true };
    }
    const response = await fetch(`${UPLOAD_API_URL}/api/upload/auth/me`, { credentials: 'include' });
    if (!response.ok && response.status !== 401 && response.status !== 403) {
      throw new Error(`Upload authentication check unavailable (${response.status})`);
    }
    return { ok: response.ok };
  }, [UPLOAD_API_URL]);

  const validateSession = useCallback(
    async ({ updateUser = false } = {}) => {
      try {
        // Flask renews the upload cookie. Wait for it before asking FastAPI to
        // validate that cookie, including on return after a long absence.
        const flaskResult = await checkFlaskAuth();
        if (!flaskResult.ok) {
          handleUnauthorized();
          return false;
        }
        // Keep the confirmed main login if the upload service is temporarily
        // unavailable. An explicit upload authentication rejection still logs out.
        if (updateUser && flaskResult.user) {
          setCurrentUser(flaskResult.user);
          setSessionExpired(false);
        }
        const uploadResult = await checkUploadAuth();

        if (!uploadResult.ok) {
          handleUnauthorized();
          return false;
        }

        return true;
      } catch (error) {
        // Network/server failures do not invalidate a signed-in session.
        console.warn('Session validation temporarily unavailable:', error);
        return true;
      }
    },
    [checkFlaskAuth, checkUploadAuth, handleUnauthorized]
  );

  // Check auth status on mount
  useEffect(() => {
    const checkAuthOnMount = async () => {
      setLoading(true);
      const ok = await validateSession({ updateUser: true });
      if (!ok) {
        setCurrentUser(null);
      }
      setLoading(false);
    };

    checkAuthOnMount();
  }, [validateSession]);

  // Periodic auth check - validates session every 5 minutes
  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      validateSession();
    }, AUTH_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [currentUser, validateSession]);

  // Validate session when returning to the tab (timers may have been paused)
  useEffect(() => {
    if (!currentUser) return;

    const handleFocus = () => {
      validateSession();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateSession();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser, validateSession]);

  
  // Login function
  const login = async (username, password) => {
    setLoading(true);
    setSessionExpired(false); // Clear expired state on new login attempt
    
    try {
      console.log("Login attempt with API URL:", API_URL);
      
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // This receives the httpOnly cookie from the server
        body: JSON.stringify({ username, password }),
      });
      
      console.log("Login response status:", response.status);
      
      const responseText = await response.text();
      console.log("Raw response:", responseText);
      
      if (responseText) {
        try {
          const data = JSON.parse(responseText);
          console.log("Parsed login response:", data);
          
          if (data.success) {
            setCurrentUser(data.user);
            // Note: session_id is now set as httpOnly cookie by the server
            // No need to store in localStorage anymore
            return { success: true };
          } else {
            return { success: false, message: data.message };
          }
        } catch (parseError) {
          console.error("Error parsing JSON:", parseError);
          return { success: false, message: "Invalid response format" };
        }
      } else {
        return { success: false, message: "Empty response from server" };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: `Connection error: ${error.message}` };
    } finally {
      setLoading(false);
    }
  };
  
  // Logout function
  const logout = async () => {
    try {
      // Server will delete the httpOnly cookie
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      setCurrentUser(null);
      setSessionExpired(false);
      // Clean up any legacy localStorage entries
      localStorage.removeItem(LEGACY_SESSION_ID_KEY);
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear local state even if server logout fails
      setCurrentUser(null);
      localStorage.removeItem(LEGACY_SESSION_ID_KEY);
    }
  };

  // Clear session expired state (called after user sees the message)
  const clearSessionExpired = () => {
    setSessionExpired(false);
  };

  /**
   * @deprecated Session token is now stored in httpOnly cookie.
   * This function is kept for backwards compatibility but returns null.
   * FastAPI requests should use credentials: 'include' instead.
   */
  const getSessionId = () => {
    console.warn('getSessionId() is deprecated. Session token is now in httpOnly cookie. Use credentials: "include" in fetch requests.');
    return null;
  };
  
  // Check if the user has a specific role
  const hasRole = (role) => {
    return currentUser && currentUser.role === role;
  };
  
  // Check if user is admin
  const isAdmin = () => {
    return hasRole('admin');
  };
  
  // Get customer information
  const getCustomerInfo = () => {
    if (!currentUser) return { customer_name: '', customer_id: '' };
    
    return { 
      customer_name: currentUser.customer_name || '',
      customer_id: currentUser.customer_id || '',
      pricing_tier: currentUser.pricing_tier || 'retail'
    };
  };
  
  const value = {
    currentUser,
    loading,
    isAuthenticated,
    sessionExpired,
    login,
    logout,
    authFetch,
    clearSessionExpired,
    getSessionId, // Deprecated but kept for backwards compatibility
    hasRole,
    isAdmin,
    getCustomerInfo,
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
